import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  CreditCard,
  DollarSign,
  Clock,
  Wallet,
  Percent,
  CalendarDays,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";
import { toastSuccess, toastError } from "@/lib/toast"; // adjust path if different

interface LoanProduct {
  id: number;
  name: string;
  installment?: string | number;
  MODE?: string;
  PRODUCT_TYPE?: string;
  minAmount?: string | number;
  maxAmount?: string | number;
  interest_rate?: string | number;
  gracePeriod?: string | number;
}

export default function ApplyLoan() {

  const [loanProducts, setLoanProducts] = useState<LoanProduct[]>([]);
  const [step, setStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    amount: "",
   
    purpose: "",
    accepted_terms: false,
  });

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingProducts(true);
        const res = await apiFetch<{ data: LoanProduct[] }>("/loan-products");
        if (!alive) return;
        setLoanProducts(res.data || []);
      } catch (error) {
        console.error(error);
        toastError("Failed to load loan products");
      } finally {
        if (alive) setLoadingProducts(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const selectedLoanProduct = useMemo(
    () => loanProducts.find((p) => p.id === selectedProduct) || null,
    [loanProducts, selectedProduct]
  );

  const formatKES = (value?: string | number) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `KES ${num.toLocaleString()}`;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      if (!selectedProduct) {
        toastError("Please select a loan product");
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!form.amount) {
        toastError("Please enter loan amount");
        return;
      }

      

      setStep(3);
      return;
    }
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      toastError("Please select a loan product");
      return;
    }

    if (!form.amount) {
      toastError("Please enter loan amount");
      return;
    }

    if (!form.accepted_terms) {
      toastError("Please agree to the loan terms and conditions");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        id: selectedProduct, // product id
        amount: Number(form.amount),
      
        purpose: form.purpose,
      };

      await apiFetch("/apply-loan", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      toastSuccess("Loan application submitted successfully");

      setStep(1);
      setSelectedProduct(null);
      setForm({
        amount: "",
       
        purpose: "",
        accepted_terms: false,
      });
    } catch (error: any) {
      console.error(error);
      toastError(error?.message || "Failed to submit loan application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Apply for a Loan</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Complete the form below to submit your loan application
        </p>
      </div>

      <div className="glass-card rounded-xl p-6">
        {step === 1 && (
          <form onSubmit={handleNext} className="space-y-4">
            <h2 className="font-semibold text-foreground">Select Loan Product</h2>

            {loadingProducts ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {loanProducts.map((product) => {
                  const active = selectedProduct === product.id;

                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setSelectedProduct(product.id)}
                      className={`text-left p-4 rounded-2xl border-2 transition-all shadow-sm ${
                        active
                          ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                          : "border-border bg-muted hover:border-primary/50 hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="font-semibold text-foreground text-base">
                            {product.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {product.PRODUCT_TYPE || "Loan Product"}
                          </p>
                        </div>

                        <div className="rounded-full px-2.5 py-1 text-[11px] font-medium bg-background text-muted-foreground border border-border">
                          {product.MODE || "Standard"}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-xl bg-background/70 border border-border p-2.5">
                          <div className="flex items-center gap-1 text-muted-foreground mb-1">
                            <Wallet className="w-3.5 h-3.5" />
                            <span>Min Amount</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {formatKES(product.minAmount)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-background/70 border border-border p-2.5">
                          <div className="flex items-center gap-1 text-muted-foreground mb-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            <span>Max Amount</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {formatKES(product.maxAmount)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-background/70 border border-border p-2.5">
                          <div className="flex items-center gap-1 text-muted-foreground mb-1">
                            <Percent className="w-3.5 h-3.5" />
                            <span>Interest</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {product.interest_rate ?? "—"}%
                          </p>
                        </div>

                        <div className="rounded-xl bg-background/70 border border-border p-2.5">
                          <div className="flex items-center gap-1 text-muted-foreground mb-1">
                            <CalendarDays className="w-3.5 h-3.5" />
                            <span>Installment</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {product.installment ?? "—"}
                          </p>
                        </div>

                        <div className="rounded-xl bg-background/70 border border-border p-2.5 col-span-2">
                          <div className="flex items-center gap-1 text-muted-foreground mb-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>Grace Period</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {product.gracePeriod ?? "0"} days
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <Button
              type="submit"
              disabled={!selectedProduct}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-semibold"
            >
              Continue →
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleNext} className="space-y-4">
            <h2 className="font-semibold text-foreground">Loan Details</h2>

            {selectedLoanProduct && (
              <div className="rounded-xl border border-border bg-muted p-4 space-y-2">
                <p className="font-medium text-foreground">{selectedLoanProduct.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedLoanProduct.PRODUCT_TYPE} • {selectedLoanProduct.MODE}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-foreground/80 text-sm font-medium">
                Loan Amount (KSh)
              </Label>
              <Input
                value={form.amount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="50000"
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11"
              />
            </div>

            

            <div className="space-y-2">
              <Label className="text-foreground/80 text-sm font-medium">
                Purpose of Loan
              </Label>
              <Input
                value={form.purpose}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, purpose: e.target.value }))
                }
                placeholder="e.g. Business Capital"
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-border text-foreground hover:bg-accent h-11"
              >
                ← Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-semibold"
              >
                Continue →
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleSubmitApplication} className="space-y-4">
            <h2 className="font-semibold text-foreground">Review & Confirm</h2>

            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Selected Product</span>
                <span className="font-medium text-foreground">
                  {selectedLoanProduct?.name || "—"}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Product ID</span>
                <span className="font-medium text-foreground">
                  {selectedProduct || "—"}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium text-foreground">
                  {form.amount ? `KES ${Number(form.amount).toLocaleString()}` : "—"}
                </span>
              </div>

             

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Purpose</span>
                <span className="font-medium text-foreground">
                  {form.purpose || "—"}
                </span>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.accepted_terms}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    accepted_terms: e.target.checked,
                  }))
                }
                className="w-4 h-4 mt-0.5 accent-primary"
              />
              <span className="text-xs text-muted-foreground">
                I confirm that the information provided is accurate and I agree
                to the <span className="text-primary">loan terms and conditions</span>.
              </span>
            </label>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                disabled={submitting}
                className="flex-1 border-border text-foreground hover:bg-accent h-11"
              >
                ← Back
              </Button>

              <Button
                type="submit"
                disabled={submitting || !form.accepted_terms}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

